import React, { useEffect, useMemo, useState } from "react";
import AppChip from "../../../shared/components/ui/AppChip";
import HorizontalAgePicker from "../../../shared/components/ui/HorizontalAgePicker";
import ListPickerSheet from "../../../shared/components/ui/ListPickerSheet";
import PickerField, { usePickerState } from "../../../shared/components/ui/PickerField";
import {
  getCitiesForCountry,
  getCountriesAlphabetical,
  getCountryByName,
  normalizeCountry,
} from "../../../shared/data/locationTaxonomy";
import {
  hasProfileGender,
  PROFILE_GENDER_CATEGORIES,
  PROFILE_ROLE_CATEGORIES,
  selectProfileGender,
  toggleProfileRoleCategory,
} from "../../../shared/data/profileTaxonomy";
import { useAuth } from "../../../shared/contexts/AuthContext";
import {
  checkUsernameAvailabilityRemote,
  normalizeUsername,
} from "../../../shared/utils/usernameValidation";
import { useOnboarding } from "../OnboardingContext";
import OnboardingWizard from "../OnboardingWizard";
import { useEditStepSave } from "../useEditStepSave";

export default function StepBasicProfile() {
  const { user } = useAuth();
  const { draft, updateDraft, goToNextStep, goToPrevStep, isEditMode, isDraftLoading, progressSaveError } = useOnboarding();
  const countries = useMemo(() => getCountriesAlphabetical(), []);
  const countryNames = useMemo(() => countries.map((country) => country.name), [countries]);
  const cities = useMemo(() => getCitiesForCountry(draft.country), [draft.country]);

  const countryPicker = usePickerState();
  const cityPicker = usePickerState();

  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, error: "" });
  const normalizedUsername = normalizeUsername(draft.username);
  const usernameLocked = isEditMode && Boolean(normalizedUsername);

  useEffect(() => {
    if (isEditMode || usernameLocked) return undefined;

    if (!normalizedUsername) {
      setUsernameStatus({ checking: false, available: null, error: "" });
      return undefined;
    }

    setUsernameStatus((prev) => ({ ...prev, checking: true, error: "" }));
    const timer = window.setTimeout(async () => {
      const result = await checkUsernameAvailabilityRemote(normalizedUsername, {
        currentUsername: draft.username,
        userId: user?.id ?? "me",
      });
      setUsernameStatus({
        checking: false,
        available: result.available,
        error: result.error || "",
      });
    }, 320);

    return () => window.clearTimeout(timer);
  }, [normalizedUsername, draft.username, user?.id, isEditMode, usernameLocked]);

  const handleCountryChange = (countryName) => {
    const country = getCountryByName(normalizeCountry(countryName));
    updateDraft({
      country: country?.name ?? countryName,
      countryCode: country?.code ?? "",
      city: "",
    });
  };

  const ageValue = draft.age === "" ? null : Number(draft.age);
  const usernameReady = usernameLocked
    ? Boolean(normalizedUsername)
    : Boolean(normalizedUsername)
      && usernameStatus.available === true
      && !usernameStatus.checking;

  const canContinue =
    usernameReady
    && Number.isFinite(ageValue)
    && ageValue >= 18
    && ageValue <= 99
    && hasProfileGender(draft.profileTypes)
    && Boolean(draft.country)
    && Boolean(draft.city);

  const { showSave, onSave, saveDisabled, saveLabel, saveError } = useEditStepSave({
    saveDisabled: !canContinue,
  });

  if (isDraftLoading) {
    return (
      <OnboardingWizard
        hideChrome
        stepKey="profile"
        title="Basic profile"
        subtitle="Loading your profile…"
        onBack={() => goToPrevStep("profile")}
        continueDisabled
        contentClassName="onboarding-wizard__content--profile"
      />
    );
  }

  return (
    <OnboardingWizard
      hideChrome
      stepKey="profile"
      title="Basic profile"
      subtitle={isEditMode
        ? "Update your profile basics."
        : "Choose your public username and tell people where you're based."}
      onBack={() => goToPrevStep("profile")}
      onContinue={() => goToNextStep("profile")}
      continueDisabled={!canContinue}
      showSave={showSave}
      onSave={onSave}
      saveDisabled={saveDisabled}
      saveLabel={saveLabel}
      contentClassName="onboarding-wizard__content--profile"
    >
      {saveError || progressSaveError ? (
        <p className="onboarding-error-banner" role="alert">
          {saveError || progressSaveError}
        </p>
      ) : null}

      <label className="onboarding-field">
        <span className="onboarding-field__label">Username</span>
        <div className={`onboarding-username-field${usernameLocked ? " onboarding-username-field--locked" : ""}`}>
          <span className="onboarding-username-field__prefix" aria-hidden>@</span>
          <input
            className={`onboarding-field__input onboarding-username-field__input${
              usernameLocked ? " onboarding-field__input--locked" : ""
            }`}
            value={draft.username}
            onChange={(e) => updateDraft({ username: normalizeUsername(e.target.value) })}
            placeholder="miazurich"
            autoComplete="username"
            spellCheck={false}
            autoCapitalize="none"
            readOnly={usernameLocked}
            aria-readonly={usernameLocked}
          />
        </div>
        {usernameLocked ? (
          <p className="onboarding-field__hint onboarding-field__hint--left">
            Usernames can&apos;t be changed after signup.
          </p>
        ) : null}
        {!usernameLocked && usernameStatus.checking ? (
          <p className="onboarding-field__hint onboarding-field__hint--left">Checking availability…</p>
        ) : null}
        {!usernameLocked && !usernameStatus.checking && usernameStatus.error ? (
          <p className="onboarding-field__hint onboarding-field__hint--left onboarding-field__hint--error">
            {usernameStatus.error}
          </p>
        ) : null}
        {!usernameLocked && !usernameStatus.checking && usernameStatus.available ? (
          <p className="onboarding-field__hint onboarding-field__hint--left onboarding-field__hint--ready">
            @{normalizedUsername} is available
          </p>
        ) : null}
      </label>

      <div className="onboarding-field">
        <span className="onboarding-field__label">Age</span>
        <HorizontalAgePicker
          value={draft.age}
          onChange={(nextAge) => updateDraft({ age: nextAge })}
        />
      </div>

      <div className="onboarding-field">
        <span className="onboarding-field__label">Gender</span>
        <div className="onboarding-chip-grid">
          {PROFILE_GENDER_CATEGORIES.map((category) => (
            <AppChip
              key={category}
              label={category}
              selected={draft.profileTypes.includes(category)}
              onClick={() => updateDraft({
                profileTypes: selectProfileGender(draft.profileTypes, category),
              })}
            />
          ))}
        </div>
      </div>

      <div className="onboarding-field">
        <span className="onboarding-field__label">Category</span>
        <div className="onboarding-chip-grid">
          {PROFILE_ROLE_CATEGORIES.map((category) => (
            <AppChip
              key={category}
              label={category}
              selected={draft.profileTypes.includes(category)}
              onClick={() => updateDraft({
                profileTypes: toggleProfileRoleCategory(draft.profileTypes, category),
              })}
            />
          ))}
        </div>
      </div>

      <div className="onboarding-field">
        <span className="onboarding-field__label">Country</span>
        <PickerField
          value={draft.country}
          placeholder="Select country"
          onClick={countryPicker.openPicker}
        />
        <ListPickerSheet
          open={countryPicker.open}
          onClose={countryPicker.closePicker}
          title="Country"
          value={draft.country}
          options={countryNames}
          onSelect={handleCountryChange}
          searchable
          searchPlaceholder="Search countries"
        />
      </div>

      <div className="onboarding-field">
        <span className="onboarding-field__label">City</span>
        <PickerField
          value={draft.city}
          placeholder={draft.country ? "Select city" : "Choose a country first"}
          onClick={cityPicker.openPicker}
          disabled={!draft.country || cities.length === 0}
        />
        <ListPickerSheet
          key={`city-${draft.countryCode || draft.country}`}
          open={cityPicker.open}
          onClose={cityPicker.closePicker}
          title="City"
          value={draft.city}
          options={cities}
          optionsKey={draft.countryCode || draft.country}
          onSelect={(city) => updateDraft({ city })}
          searchable
          searchPlaceholder="Search cities"
        />
      </div>
    </OnboardingWizard>
  );
}
